import { Company, CompanyForm } from '../types/company';
import { http } from './http';

/**
 * 투자처 — /api/companies. 화면은 섹터를 이름(문자열)으로 쓰고 DB 는 sector_id 로 든다.
 * 없는 섹터 이름이 오면 섹터를 만든다(설정 › 산업 섹터에 생긴다).
 */
interface Sector { id: number; name: string }
type Row = Omit<Company, 'sector'> & { sectorId: number | null; [k: string]: unknown };

let sectorCache: Sector[] | null = null;
async function sectors(): Promise<Sector[]> {
  if (!sectorCache) sectorCache = await http.get<Sector[]>('/sectors');
  return sectorCache;
}
async function sectorId(name: string | null | undefined): Promise<number | null> {
  const n = (name ?? '').trim();
  if (!n) return null;
  const hit = (await sectors()).find((s) => s.name === n);
  if (hit) return hit.id;
  const made = await http.post<Sector>('/sectors', { name: n, sortOrder: 99 });
  sectorCache = null;
  return made.id;
}
async function toUi(r: Row): Promise<Company & Record<string, unknown>> {
  const s = (await sectors()).find((x) => x.id === r.sectorId);
  return { ...(r as any), sector: s?.name ?? null, note: r.note ?? '' };
}
async function fromUi(p: Partial<Company> & Record<string, unknown>) {
  const { id, createdAt, updatedAt, deletedAt, sector, ...rest } = p as any;
  return 'sector' in p ? { ...rest, sectorId: await sectorId(sector) } : rest;
}

export const companyApi = {
  async list(): Promise<Company[]> {
    sectorCache = null;
    const rows = await http.get<Row[]>('/companies');
    const out = await Promise.all(rows.map(toUi));
    return out.sort((a, b) => a.name.localeCompare(b.name));
  },
  async get(id: number): Promise<Company | undefined> {
    return toUi(await http.get<Row>(`/companies/${id}`));
  },
  async update(id: number, patch: Partial<Company> & Record<string, unknown>): Promise<Company> {
    return toUi(await http.patch<Row>(`/companies/${id}`, await fromUi(patch)));
  },
  async create(form: CompanyForm): Promise<Company> {
    return toUi(await http.post<Row>('/companies', {
      name: form.name.trim(), bizNo: form.bizNo.trim() || null, sectorId: await sectorId(form.sector),
      foundedOn: form.foundedOn || null, homepage: form.homepage.trim() || null, note: form.note.trim(),
    }));
  },
};
